import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/designSystem.css';

/* Liste déroulante accessible — STANDARD (2026-09-11). Remplace le `<select>`
   natif là où son menu ne se laisse pas placer : Safari l'ouvre en fenêtre
   flottante centrée sur l'option choisie, Chrome sous le champ, et aucun CSS
   n'y change rien. Ici le menu est rendu en PORTAIL, sous le champ, aligné à
   gauche et de sa largeur, 6 px plus bas ; il passe au-dessus seulement si la
   place manque dessous, et défile au-delà de 280 px. Rien ne le coupe, pas
   même une fenêtre à `overflow: auto`.

   Clavier : flèches, Début/Fin, Entrée ou Espace pour choisir, Échap pour
   fermer, Tab ferme ; le focus revient sur le champ.

     <Listbox value={v} onChange={setV} options={[{ value, label, disabled? }]} />

   `options` accepte aussi { group: 'titre' } pour un intertitre.

   `searchable` (2026-10-20) : une liste longue (une soixantaine de comptes) se
   filtre par le nom, en tête du menu, sans tenir compte des accents ni de la
   casse. Le champ prend le focus à l'ouverture ; Entrée choisit la ligne
   active, Espace reste une lettre de la recherche. */
const norm = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function Listbox({ value, onChange, options: allOptions = [], placeholder = '', disabled = false, className = '', id,
  searchable = false, searchPlaceholder = '' }) {
  const uid = useId();
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [q, setQ] = useState('');
  const searchRef = useRef(null);
  const courant = allOptions.find((o) => !o.group && String(o.value) === String(value));
  /* La recherche ne garde que les choix qui contiennent le texte, et les
     intertitres qui en gardent au moins un. */
  const options = useMemo(() => {
    const needle = norm(q.trim());
    if (!searchable || !needle) return allOptions;
    const out = [];
    allOptions.forEach((o, i) => {
      if (o.group) {
        const next = allOptions.slice(i + 1).findIndex((x) => x.group);
        const own = allOptions.slice(i + 1, next < 0 ? undefined : i + 1 + next);
        if (own.some((x) => norm(x.label).includes(needle))) out.push(o);
      } else if (norm(o.label).includes(needle)) out.push(o);
    });
    return out;
  }, [allOptions, q, searchable]);
  const choix = options.filter((o) => !o.group);
  const [actif, setActif] = useState(-1);

  const placer = () => {
    const r = btnRef.current && btnRef.current.getBoundingClientRect();
    if (!r) return;
    const haut = Math.min(280, 8 + (searchable ? 44 : 0) + choix.length * 32 + options.filter((o) => o.group).length * 24);
    const dessous = window.innerHeight - r.bottom - 6;
    const enHaut = dessous < haut && r.top - 6 > dessous;
    /* LA LISTE PREND LA LARGEUR DE SES CHOIX (2026-09-30) : au moins celle du
       champ, au plus ce qui reste jusqu'au bord de l'écran ; un libellé plus
       long passe à la ligne au lieu d'être coupé (« En base installée, à un
       revenu par p… » ne se lisait pas). */
    const room = window.innerWidth - r.left - 12;
    setPos({ left: r.left, minWidth: Math.min(Math.max(r.width, 200), room), maxWidth: room,
      top: enHaut ? undefined : r.bottom + 6,
      bottom: enHaut ? window.innerHeight - r.top + 6 : undefined,
      maxHeight: Math.max(120, Math.min(280, enHaut ? r.top - 12 : dessous)) });
  };
  const ouvrir = () => {
    if (disabled) return;
    placer();
    setQ('');
    setActif(Math.max(0, allOptions.filter((o) => !o.group).findIndex((o) => String(o.value) === String(value))));
    setOpen(true);
  };
  const fermer = (rendre = true) => { setOpen(false); if (rendre && btnRef.current) btnRef.current.focus(); };
  const choisir = (o) => { if (o.disabled) return; onChange(o.value); fermer(); };

  useLayoutEffect(() => { if (open) placer(); }, [open]); // eslint-disable-line
  useEffect(() => { if (open && searchable && searchRef.current) searchRef.current.focus(); }, [open, searchable]);
  useEffect(() => {
    if (!open) return undefined;
    const clic = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      fermer(false);
    };
    const bouge = () => placer();
    document.addEventListener('mousedown', clic);
    window.addEventListener('resize', bouge);
    window.addEventListener('scroll', bouge, true);
    return () => { document.removeEventListener('mousedown', clic); window.removeEventListener('resize', bouge); window.removeEventListener('scroll', bouge, true); };
  }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const el = menuRef.current.querySelector(`[data-i="${actif}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, actif]);

  const clavier = (e) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); ouvrir(); }
      return;
    }
    const enRecherche = searchable && e.target === searchRef.current;
    const suivant = (d) => {
      let i = actif;
      for (let k = 0; k < choix.length; k++) { i = (i + d + choix.length) % choix.length; if (!choix[i].disabled) break; }
      setActif(i);
    };
    if (e.key === 'ArrowDown') { e.preventDefault(); suivant(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); suivant(-1); }
    else if (e.key === 'Home' && !enRecherche) { e.preventDefault(); setActif(0); }
    else if (e.key === 'End' && !enRecherche) { e.preventDefault(); setActif(choix.length - 1); }
    else if (e.key === 'Enter' || (e.key === ' ' && !enRecherche)) { e.preventDefault(); if (choix[actif]) choisir(choix[actif]); }
    else if (e.key === 'Escape') { e.preventDefault(); fermer(); }
    else if (e.key === 'Tab') fermer(false);
  };

  let n = -1;
  return (
    <>
      <button type="button" ref={btnRef} id={id} disabled={disabled}
              className={`lk-lb${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
              role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-controls={`${uid}-menu`}
              onClick={() => (open ? fermer() : ouvrir())} onKeyDown={clavier}>
        <span className={`lk-lb__val${courant ? '' : ' is-empty'}`}>{courant ? courant.label : placeholder}</span>
        <span className="lk-lb__chev" aria-hidden="true">⌄</span>
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} id={`${uid}-menu`} role="listbox" className="lk-lb__menu"
             style={{ left: pos.left, minWidth: pos.minWidth, maxWidth: pos.maxWidth, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
             onKeyDown={clavier}>
          {searchable && (
            <input ref={searchRef} type="search" className="lk-lb__search" value={q}
                   placeholder={searchPlaceholder} aria-label={searchPlaceholder || undefined}
                   onChange={(e) => { setQ(e.target.value); setActif(0); }} />
          )}
          {options.map((o, k) => {
            if (o.group) return <p key={`g${k}`} className="lk-lb__grp">{o.group}</p>;
            n += 1;
            const i = n;
            return (
              <button key={String(o.value)} type="button" role="option" data-i={i} tabIndex={-1}
                      aria-selected={courant && String(courant.value) === String(o.value)} disabled={o.disabled}
                      className={`lk-lb__opt${i === actif ? ' is-active' : ''}${courant && String(courant.value) === String(o.value) ? ' is-selected' : ''}`}
                      onMouseEnter={() => setActif(i)} onClick={() => choisir(o)}>
                <span className="lk-lb__tick" aria-hidden="true">{courant && String(courant.value) === String(o.value) ? '✓' : ''}</span>
                {o.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
